import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import { credentialSaveErrorMessage, useSavePortalCredentials } from 'hooks/useSavePortalCredentials';
import { simpleDate } from 'lib/date';
import PortalConnection from 'models/PortalConnection';
import { BodySmall, BodySmallBold } from 'styles/typography';

interface Props {
  connection: PortalConnection;
  // null while the probe is still running — recording needs the extension in EVERY mode, so the
  // sweep row waits for the answer rather than rendering as unavailable and then flipping.
  extensionPresent: boolean | null;
  onBack: () => void;
  onSaved: () => void;
  onRecordSweep: () => void;
}

/**
 * One portal, everything a user can configure about it: the headless sign-in and the recorded
 * instructions. Credentials render for headless mode ONLY — extension mode keeps the password in
 * the browser's own storage, where Olio cannot see it and has nothing to collect.
 *
 * The credentials form has no recording prerequisite: the api used to 422 a save without an owned
 * retrieval script and no longer does (olio-api #3352), so credentials-first — the order the
 * design draws — works.
 */
export default function PortalDetailStep({ connection, extensionPresent, onBack, onSaved, onRecordSweep }: Props) {
  const name = connection.displayName;

  return (
    <>
      <Headline data-testid='portal-detail-headline'>
        {connection.isHeadless
          ? `Olio will sign in to ${name} as you and pull referral details on its own, so you don’t need to open the portal for each referral.`
          : `Olio works ${name} in your browser using the instructions you record, so it can find and read referrals for you.`}
      </Headline>

      {connection.isHeadless && <CredentialsSection connection={connection} onSaved={onSaved} />}

      <InstructionsBlock connection={connection} extensionPresent={extensionPresent} onRecordSweep={onRecordSweep} />

      <BackRow>
        <Button variant='ghost' size='small' onClick={onBack} data-testid='portal-detail-back'>
          Back
        </Button>
      </BackRow>
    </>
  );
}

function CredentialsSection({ connection, onSaved }: { connection: PortalConnection; onSaved: () => void }) {
  const name = connection.displayName;
  const [username, setUsername] = useState(connection.credentials?.username ?? '');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const { mutate, isPending } = useSavePortalCredentials({ onSaved });

  const submit = () => {
    setUsernameError(username ? '' : 'This field is required');
    setPasswordError(password ? '' : 'This field is required');
    setSubmitError('');
    if (!username || !password) return;
    mutate(
      { sourceSystem: connection.sourceSystem, username, password },
      { onError: (error) => setSubmitError(credentialSaveErrorMessage(error)) }
    );
  };

  return (
    <Section data-testid='portal-credentials-section'>
      <SectionTitle>SIGN-IN</SectionTitle>
      <Explainer>
        It is encrypted in your browser before it is sent, and saving replaces any sign-in saved for {name} before.
      </Explainer>
      <Fields>
        <InputGroup title='Username' error={usernameError}>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete='off'
            data-testid='portal-credentials-username'
          />
        </InputGroup>
        <InputGroup title='Password' error={passwordError}>
          <Input
            type={reveal ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete='new-password'
            data-testid='portal-credentials-password'
          />
        </InputGroup>
        <RevealToggle
          type='button'
          onClick={() => setReveal((shown) => !shown)}
          data-testid='portal-credentials-reveal'>
          {reveal ? 'Hide password' : 'Show password'}
        </RevealToggle>
      </Fields>
      {submitError && <ErrorText data-testid='portal-credentials-error'>{submitError}</ErrorText>}
      <Actions>
        <Button size='small' onClick={submit} loading={isPending} data-testid='portal-credentials-save'>
          Save and Test Login
        </Button>
      </Actions>
    </Section>
  );
}

/**
 * The two recordings a portal can have, one row each with its own state — a single
 * `Re-Record Instructions` button cannot say which purpose it means, and cannot show that one is
 * stale while the other was recorded yesterday.
 *
 * Only the sweep row acts this pass. Recording a retrieval means walking ONE specific referral,
 * and a settings modal has none — so that row reports its state and sends the user to a task.
 */
function InstructionsBlock({
  connection,
  extensionPresent,
  onRecordSweep,
}: {
  connection: PortalConnection;
  extensionPresent: boolean | null;
  onRecordSweep: () => void;
}) {
  const sweep = connection.recording('discovery');
  const retrieval = connection.recording('retrieval');

  // The portal home a sweep is recorded against is not stored anywhere as portal-level data: the
  // api itself reads it off the newest discovery script's entry URL when it starts a scan. So a
  // re-record has a sound source and a FIRST recording has none.
  const sweepBlocked = !sweep.recorded
    ? 'Olio has no portal address to record against yet — the first sweep is recorded from a portal scan.'
    : extensionPresent === false
      ? 'Recording needs the Olio browser extension.'
      : null;

  return (
    <Section data-testid='portal-instructions'>
      <SectionTitle>INSTRUCTIONS</SectionTitle>
      <Row data-testid='instructions-row-discovery'>
        <RowText>
          <RowName>Finding referrals</RowName>
          <RowState data-testid='instructions-state-discovery'>
            {recordedState(sweep.recordedAt, sweep.recorded)}
          </RowState>
          {sweepBlocked && <RowNote data-testid='instructions-blocked-discovery'>{sweepBlocked}</RowNote>}
        </RowText>
        <Button
          size='small'
          variant='outline-primary'
          disabled={!!sweepBlocked || extensionPresent === null}
          onClick={onRecordSweep}
          data-testid='instructions-record-discovery'>
          {/* "Re-Record" is a lie on a portal that has never been recorded — and that is exactly
              the state where the button is disabled for want of a portal address, so the label
              was promising to redo something that had never happened. */}
          {sweep.recorded ? 'Re-Record' : 'Record'}
        </Button>
      </Row>
      <Row data-testid='instructions-row-retrieval'>
        <RowText>
          <RowName>Reading a referral</RowName>
          <RowState data-testid='instructions-state-retrieval'>
            {recordedState(retrieval.recordedAt, retrieval.recorded)}
          </RowState>
          <RowNote>Recorded while working a task — it follows one referral through the portal.</RowNote>
        </RowText>
      </Row>
    </Section>
  );
}

function recordedState(recordedAt: Date | null, recorded: boolean): string {
  if (!recorded) return 'Not recorded';
  return recordedAt ? `Last recorded ${simpleDate(recordedAt)}` : 'Recorded';
}

const Headline = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
  display: block;
  margin-bottom: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
`;

const SectionTitle = styled(BodySmallBold)`
  font-size: 11px;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.black50};
`;

const Explainer = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

const RevealToggle = styled.button`
  align-self: flex-start;
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ErrorText = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.red100};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowName = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black100};
`;

const RowState = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const RowNote = styled(BodySmall)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black50};
`;

const BackRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
`;
